/**
 * How table D's classes (and part 10's pending line, part 10a's paragraph look) look in the editor. Structure only (italics, weights, sizes, alignment, the quote rule);
 * colours, fonts and editor themes are part 11's (decision 16). CodeMirror mounts it as a constructed style sheet
 * where the browser has them; the part 09 browser test checks it applies under the app's CSP.
 */
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { LIVE_CLASS } from './live.ts';
import { LOOK_CLASS } from './look.ts';

// Part 11c, A6: markup is muted by colour, the host's `--ct-muted` (the app's 7:1 muted ink), never by opacity, which
// fades it below the contrast any text needs; without the variable it is the text's own colour.
const muted = { color: 'var(--ct-muted, inherit)' };

/** Table D's classes and how each looks. */
export const LIVE_STYLES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  [`.${LIVE_CLASS.markup}`]: muted,
  [`.${LIVE_CLASS.emphasis}`]: { fontStyle: 'italic' },
  [`.${LIVE_CLASS.strong}`]: { fontWeight: 'bold' },
  [`.${LIVE_CLASS.smallCaps}`]: { fontVariantCaps: 'small-caps' },
  [`.${LIVE_CLASS.link}`]: { textDecoration: 'underline' },
  [`.${LIVE_CLASS.image}`]: { fontStyle: 'italic', ...muted },
  [`.${LIVE_CLASS.footnoteReference}`]: { verticalAlign: 'super', fontSize: '0.75em' },
  [`.${LIVE_CLASS.footnoteLabel}`]: muted,
  [`.${LIVE_CLASS.footnote}`]: { fontSize: '0.9em' },
  [`.${LIVE_CLASS.heading}`]: { fontWeight: 'bold' },
  [`.${LIVE_CLASS.heading}-1`]: { fontSize: '1.6em' },
  [`.${LIVE_CLASS.heading}-2`]: { fontSize: '1.35em' },
  [`.${LIVE_CLASS.heading}-3`]: { fontSize: '1.2em' },
  [`.${LIVE_CLASS.quote}`]: { borderLeft: '3px solid currentColor', paddingLeft: '1em' },
  [`.${LIVE_CLASS.listMark}`]: muted,
  [`.${LIVE_CLASS.centered}`]: { textAlign: 'center' },
  [`.cm-line.${LIVE_CLASS.sceneBreak}`]: { textAlign: 'center' },
  [`.${LIVE_CLASS.linkDefinition}`]: muted,
  // Part 10, N1 and N6: a pending block or break shows as a new line with its own caret; the browser's caret, left at
  // the line end before it, is hidden meanwhile.
  '&.ct-pending .cm-content': { caretColor: 'transparent' },
  '.ct-pending-caret': { display: 'inline-block', width: '1px', height: '1.2em', verticalAlign: 'text-bottom', background: 'currentColor' },
  // Part 10a, C9: a pending line a press keeps shows no caret, though it keeps the caret's room.
  '.ct-pending-caret.ct-pending-held': { visibility: 'hidden' },
  // Part 10a: a separator never shows (L); the line after one has the gap above it (G); a first line its indent (J).
  // The lengths are the look's CSS variables, which the look plugin sets.
  [`.cm-line.${LOOK_CLASS.separator}`]: { height: '0', overflow: 'hidden' },
  // Table C: the one the cursor stands on shows, as the line typing there makes.
  [`.cm-line.${LOOK_CLASS.separator}.${LOOK_CLASS.caret}`]: { height: 'auto', overflow: 'visible' },
  [`.cm-line.${LOOK_CLASS.gap}`]: { paddingTop: 'var(--ct-gap, 0)' },
  [`.cm-line.${LOOK_CLASS.indent}`]: { textIndent: 'var(--ct-indent, 0)' },
};

/** The editor's styles for table D. */
export const liveTheme: Extension = EditorView.baseTheme(LIVE_STYLES);
